import React, { useCallback, useState, useEffect } from 'react';
import { useFetch } from '../../../../utils/hooks';
import './CostPane.scss';
import { Empty, Spin, Popover, Form, Input, Button, message, Select } from 'antd';
import { ReactSVG } from 'react-svg';
import { getShipmentDetailsCost, updatePaymentItem, createPaymentItem } from '../../../../services/shipments';
import { getCostIcons, getPanesIcon } from '../../../../utils/images';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useFormData } from '../../../../context/FormDataContext';

const CostPane = ({ shipment }) => {
  const { data, loading, refetch } = useFetch(useCallback(() => getShipmentDetailsCost(shipment.id), [shipment.id]));
  const [editingItem, setEditingItem] = useState(null);
  const [creatingItem, setCreatingItem] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const cost = data?.cost || {};

  const handleEdit = item => {
    setEditingItem(item);
  };

  const handleUpdate = async values => {
    try {
      setSubmitting(true);
      await updatePaymentItem(editingItem.id, values);
      message.success('Price updated successfully');
      setEditingItem(null);
      refetch();
    } catch (error) {
      message.error(error.response?.data?.errors?.[0] || 'Failed to update price');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreate = async values => {
    try {
      setSubmitting(true);
      await createPaymentItem({
        ...values,
      });
      message.success('Cost created successfully');
      setCreatingItem(false);
      refetch();
    } catch (error) {
      message.error(error.response?.data?.errors?.[0] || 'Failed to create cost item');
    } finally {
      setSubmitting(false);
    }
  };

  const EditPriceForm = ({ item }) => {
    const [form] = Form.useForm();

    useEffect(() => {
      if (item) {
        form.setFieldsValue({
          amount: item.price_amount,
        });
      }
    }, [item, form]);

    return (
      <Form
        form={form}
        onFinish={handleUpdate}
        layout="vertical"
        style={{ width: 200 }}
      >
        <Form.Item
          name="amount"
          label="Price"
          rules={[{ required: true, message: 'Please enter price amount' }]}
        >
          <Input type="number" addonAfter={item.currency} />
        </Form.Item>
        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            size="small"
            loading={submitting}
          >
            Update
          </Button>
        </Form.Item>
      </Form>
    );
  };

  const CreatePaymentForm = () => {
    const [form] = Form.useForm();
    const { currencies } = useFormData();

    useEffect(() => {
      if (currencies?.currencies) {
        const usdCurrency = currencies.currencies.find(c => c.name === 'USD');
        if (usdCurrency) {
          form.setFieldValue('currency_id', usdCurrency.id);
        }
      }
    }, [currencies, form]);

    return (
      <Form
        form={form}
        onFinish={handleCreate}
        layout="vertical"
        style={{ width: 300 }}
      >
        <Form.Item
          name="line_item_id"
          label="Line Item"
          rules={[{ required: true, message: 'Please select a line item' }]}
        >
          <Select
            placeholder="Select line item"
            options={shipment.line_items?.map(item => ({
              label: item.name, value: item.id,
            })) || []}
          />
        </Form.Item>

        <Form.Item
          name="payment_type"
          label="Payment Type"
          rules={[{ required: true, message: 'Please select payment type' }]}
        >
          <Select
            placeholder="Select payment type"
            options={getPaymentTypeOptions()}
          />
        </Form.Item>

        <Form.Item
          name="currency_id"
          label="Currency"
          rules={[{ required: true, message: 'Please select a currency' }]}
        >
          <Select
            placeholder="Select currency"
            allowClear
            showSearch
            optionFilterProp="label"
            filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            options={currencies?.currencies?.map(currency => ({
              label: currency.name, value: currency.id,
            })) || []}
          />
        </Form.Item>

        <Form.Item
          name="amount"
          label="Amount (Per container)"
          rules={[{ required: true, message: 'Please enter amount' }]}
        >
          <Input type="number" />
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            size="small"
            loading={submitting}
          >
            Create
          </Button>
        </Form.Item>
      </Form>
    );
  };

  const getPaymentTypeOptions = () => {
    const options = [];

    // Freight-related options
    if (shipment.pol?.id || shipment.pod?.id) {
      options.push(
        { label: 'Sea Freight', value: 'Sea Freight' },
        { label: 'Surcharge', value: 'Surcharge' },
        { label: 'BL', value: 'BL' },
        { label: 'THC', value: 'THC' },
        { label: 'DTHC', value: 'DTHC' },
      );
    }

    // Origin inland-related options
    if (shipment.pickup?.id) {
      options.push(
        { label: 'Inland Origin', value: 'Inland Origin' },
        { label: 'Pol Official Receipts', value: 'Pol Official Receipts' },
      );
    }

    // Destination inland-related options
    if (shipment.dropoff?.id) {
      options.push(
        { label: 'Inland Destination', value: 'Inland Destination' },
        { label: 'Pod Official Receipts', value: 'Pod Official Receipts' },
      );
    }

    // Common options
    options.push(
      { label: 'Pol Customs', value: 'Pol Customs' },
      { label: 'Pod Customs', value: 'Pod Customs' },
      { label: 'Service Fee', value: 'Service Fee' },
      { label: 'Others', value: 'Others' },
    );

    return options;
  };

  if (loading) {
    return <Spin size="large" className="spinner" />;
  }

  const ORDER = [
    { key: 'freight', header: 'Sea Freight', icon: 'sea' },
    { key: 'surcharge', header: 'Surcharge', icon: 'sea' },
    { key: 'bl', header: 'BL', icon: 'sea' },
    { key: 'thc', header: 'THC', icon: 'thc' },
    { key: 'dthc', header: 'DTHC', icon: 'thc', optional: true },
    { key: 'inland_origin', header: 'Inland Trucking', icon: 'truck', optional: true },
    { key: 'pol_official', header: 'Pol Trucking Official Receipts', icon: 'truck', optional: true },
    { key: 'pol_customs', header: 'Pol Customs', icon: 'customs', optional: true },
    { key: 'pod_customs', header: 'Pod Customs', icon: 'customs', optional: true },
    { key: 'inland_destination', header: 'Inland Trucking', icon: 'truck', optional: true },
    { key: 'pod_official', header: 'Pod Trucking Official Receipts', icon: 'truck', optional: true },
    { key: 'others', header: 'Others', icon: 'sea', optional: true },
    { key: 'fee', header: 'Service Fee', icon: 'sea', optional: true },
  ];

  const editableCost = cost?.editable;
  return (<div className="CostPane pane">
    <div className="pane__header">
      <ReactSVG src={getPanesIcon('cost')} />
      <div className="pane__title">Shipment Cost</div>
    </div>
    {editableCost && (<Popover
      content={<CreatePaymentForm />}
      title="Add New Cost"
      trigger="click"
      open={creatingItem}
      onOpenChange={visible => !visible && setCreatingItem(false)}
    >
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => setCreatingItem(true)}
        className="add-cost-btn"
      >
        Add New Cost
      </Button>
    </Popover>)}
    {!cost?.total ? (<div className="CostCard">
      <Empty />
    </div>) : (<div className="CostCard">
      <div className="headline">
        <div className="headline__title">Quotation Ref</div>
        <div className="headline__subtitle">{cost.q_ref}</div>
      </div>

      <div className="freight-price">
        <div className="freight-price__title">Total</div>
        <div className="freight-price__subtitle">{`$${cost?.total}`}</div>
      </div>

      {cost?.profit && (<div className="freight-price">
        <div className="freight-price__title">Profit</div>
        <div className="freight-price__subtitle">{`$${cost?.profit}`}</div>
      </div>)}

      <div className="items">
        {ORDER.map(({ key, header, icon, optional }, itemIndex) => {
          return cost.details && cost.details[key]?.total === '$0' && optional ? (<></>) : (
            <div key={itemIndex} className="item-wrapper">
              <div className="item-total">
                <div className="item-total__title">
                  <img src={getCostIcons(icon)} />
                  <div>{header}</div>
                </div>
                <div>{cost.details && cost.details[key]?.total}</div>
              </div>
              {cost.details && cost.details[key]?.lines.map(
                ({ headline, price, price_amount, id, editable, currency }, index) => (
                  <div key={`${key}-${itemIndex}-${index}`} className="item">
                    <div className="item__title">
                      <img src={getCostIcons('container')} />
                      <div>{headline}</div>
                    </div>
                    <div className="item__price">
                      {editableCost && editable && (<Popover
                        content={<EditPriceForm
                          item={{
                            id, price_amount, currency,
                          }}
                        />}
                        title={headline}
                        trigger="click"
                        open={editingItem?.id === id}
                        onOpenChange={visible => !visible && setEditingItem(null)}
                      >
                        <Button
                          type="text"
                          icon={<EditOutlined />}
                          size="small"
                          onClick={() => handleEdit({ id, price_amount, currency })}
                        />
                      </Popover>)}
                      {price}
                    </div>
                  </div>
                ))}
            </div>
          );
        })}
      </div>
    </div>)}
  </div>);
};

export default CostPane;
